/**
 * Explanation bubble for `.aa-tooltip[data-tooltip]` spans (rich-text format
 * „Tooltip / Erklärung“). Hover/focus shows it, click/Enter pins it, Esc closes.
 */
const SELECTOR = '.aa-tooltip[data-tooltip]';
const hoverCapable = window.matchMedia('(hover: hover)').matches;

let bubble;
let inner;
let arrow;
let pinned = null;

// event.target can be the document itself (synthetic events, text nodes) – no closest() there.
const closestTooltip = (event) => (event.target instanceof Element ? event.target.closest(SELECTOR) : null);

const build = () => {
  if (bubble) {
    return;
  }
  bubble = document.createElement('div');
  bubble.className = 'aa-tooltip__bubble';
  bubble.setAttribute('role', 'tooltip');
  inner = document.createElement('span');
  arrow = document.createElement('span');
  arrow.className = 'aa-tooltip__arrow';
  bubble.append(inner, arrow);
  document.body.appendChild(bubble);
};

const place = (el) => {
  const margin = 8;
  const gap = 10;
  bubble.style.left = '0px';
  bubble.style.top = '0px';
  const width = bubble.offsetWidth;
  const height = bubble.offsetHeight;
  const rects = el.getClientRects();
  const rect = rects.length ? rects[0] : el.getBoundingClientRect();
  const centerX = rect.left + rect.width / 2;

  const left = Math.max(margin, Math.min(centerX - width / 2, window.innerWidth - width - margin));
  let top = rect.top - height - gap;
  let below = false;
  if (top < margin) {
    top = rect.bottom + gap;
    below = true;
  }

  bubble.style.left = `${Math.round(left)}px`;
  bubble.style.top = `${Math.round(top)}px`;
  bubble.dataset.pos = below ? 'below' : 'above';
  arrow.style.left = `${Math.round(Math.max(16, Math.min(centerX - left, width - 16)))}px`;
};

const show = (el) => {
  build();
  const text = el.getAttribute('data-tooltip') || '';
  if (!text) {
    return;
  }
  inner.textContent = text;
  bubble.classList.add('is-visible');
  place(el);
};

const hide = () => bubble?.classList.remove('is-visible');

const toggle = (el) => {
  if (pinned === el) {
    pinned = null;
    hide();
  } else {
    pinned = el;
    show(el);
  }
};

const enhance = () => {
  document.querySelectorAll(SELECTOR).forEach((el) => {
    if (el.dataset.aaTooltipReady) {
      return;
    }
    el.dataset.aaTooltipReady = '1';
    el.setAttribute('tabindex', '0');
    el.setAttribute('role', 'button');
    el.setAttribute('aria-label', el.getAttribute('data-tooltip') || '');
  });
};

document.addEventListener('mouseover', (event) => {
  if (!hoverCapable || pinned) {
    return;
  }
  const target = closestTooltip(event);
  if (target) {
    show(target);
  }
});

document.addEventListener('mouseout', (event) => {
  if (!hoverCapable || pinned) {
    return;
  }
  if (closestTooltip(event)) {
    hide();
  }
});

document.addEventListener('click', (event) => {
  const target = closestTooltip(event);
  if (target) {
    event.preventDefault();
    toggle(target);
    return;
  }
  if (pinned) {
    pinned = null;
    hide();
  }
});

document.addEventListener('keydown', (event) => {
  const target = closestTooltip(event);
  if (target && (event.key === 'Enter' || event.key === ' ')) {
    event.preventDefault();
    toggle(target);
  }
  if (event.key === 'Escape' && pinned) {
    pinned = null;
    hide();
  }
});

document.addEventListener('focusin', (event) => {
  const target = closestTooltip(event);
  if (target && !pinned) {
    show(target);
  }
});

document.addEventListener('focusout', (event) => {
  if (closestTooltip(event) && !pinned) {
    hide();
  }
});

const reposition = () => {
  if (pinned) {
    place(pinned);
  } else {
    hide();
  }
};
window.addEventListener('scroll', reposition, true);
window.addEventListener('resize', reposition);

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', enhance);
} else {
  enhance();
}
